<template>
  <div id="app" :class="{'mobile-app-banner-visible': showMobileAppBanner}" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">

     <!-- loading -->
      <div v-if="state === false" class="custom-loading" role="status" aria-live="polite">
          <div class="loading-glow loading-glow-primary" aria-hidden="true"></div>
          <div class="loading-glow loading-glow-accent" aria-hidden="true"></div>

          <div class="loading-content">
            <img
              class="loading-logo"
              src="/images/favicon.png"
              alt="Gstocky"
            >
            <p class="loading-tagline">{{ $t('manage_your_business') }}</p>

            <div class="loading-modules">
              <div
                v-for="(module, index) in loadingModules"
                :key="module.title"
                class="loading-module"
                :style="{
                  '--module-delay': `${index * 120}ms`,
                  '--module-color': module.color,
                  '--module-background': module.background
                }"
                role="img"
                :aria-label="$t(module.title)"
                :title="$t(module.title)"
              >
                <AppIcon :name="module.icon" class="bi" :class="module.icon" aria-hidden="true" />
              </div>
            </div>

            <div class="loading-progress" aria-hidden="true">
              <span></span>
            </div>
            <div class="loading-text">{{ $t('loading') }}</div>
          </div>
      </div>
      <!-- loading -->

      <div v-else>

        <!-- app -->
          <CustomSideBar v-if="$route.meta.sidebar!==false && !kioskMode" />
          <CustomNav v-if="$route.meta.sidebar!==false && !kioskMode" />
          <MobileAppBanner
            v-if="showMobileAppBanner"
            :platform="mobileAppPlatform"
            @dismiss="dismissMobileAppBanner"
          />
          <FeedbackToast v-if="showFeedbackToast" />
          <UpdateAnnouncementModal v-if="showUpdateAnnouncement" />

          <!-- body -->
          <transition name="appFade" mode="out-in">
                <div v-if="['/login', '/signup', '/reset-password', '/lp'].includes($route.path) || $route.path.startsWith('/admin')">
                  <router-view />
                </div>
                <div v-else :class="isToken === false ? '' : $store.getters.NAV_FULL_SIZE === true && $route.meta.sidebar!==false ?  'mx-auto custom-sidebar-space' : 'mx-auto custom-sidebar-full-space'" >
                  <router-view />
                </div>
          </transition>
          <!-- body -->
        <!-- app -->

      </div>

  </div>
</template>

<script>

import CustomSideBar from './components/layout/CustomSideBar.vue'
import CustomNav from './components/layout/CustomNav.vue'
import FeedbackToast from './components/FeedbackToast.vue'
import UpdateAnnouncementModal from './components/UpdateAnnouncementModal.vue'
import MobileAppBanner from './components/MobileAppBanner.vue'
import helper from './helper'

// The feedback prompt is a legacy-user campaign. Accounts created after the
// campaign launched should not be asked for feedback during or after onboarding.
const FEEDBACK_CAMPAIGN_CUTOFF = Date.parse('2026-08-03T11:41:17.000Z')
const MOBILE_APP_BANNER_DISMISSED_KEY = 'gstocky_mobile_app_banner_dismissed'

export default {
  name: 'App',
  components : {
    CustomSideBar,
    CustomNav,
    FeedbackToast,
    UpdateAnnouncementModal,
    MobileAppBanner,
  },
  data(){
    return {
      isToken : false,
      loadingImage : false,
      loading : false,
      state : false,
      loadingModules: [
        {
          title: 'dashboard_title',
          icon: 'bi-speedometer',
          color: '#2563eb',
          background: '#eaf2ff'
        },
        {
          title: 'inventory_title',
          icon: 'bi-box2',
          color: '#d97706',
          background: '#fff4dc'
        },
        {
          title: 'sales_title',
          icon: 'bi-receipt',
          color: '#059669',
          background: '#e5f8f1'
        },
        {
          title: 'crm_title',
          icon: 'bi-people',
          color: '#7c3aed',
          background: '#f1eafe'
        }
      ],
      createModal :null,
      business : {
        name : '',
        email : '',
        website : '',
        phone : '',
        address : '',
        metadata : {},
        currency : 'dh',
        language : 'fr',
        country :'morocco',
        extra_fields : []
      },
      step : 'general_info',
      stepIndex : 1,
      template : null,
      logoFile : null,
      showAlert : true,
      mobileAppPlatform: null,
      mobileAppBannerDismissed: false,
      isNew  : false,
      countries: []
    }
  },
  
  async created(){

    if(localStorage.getItem('currentLang')){
      this.$i18n.locale = localStorage.getItem('currentLang')
    }

    if(localStorage.getItem('access_token')){
      this.isToken = true
    }

    //check token in url
    if(this.$route.query.at){
      localStorage.setItem('access_token',this.$route.query.at)
    }

    // Only handle lang query for authenticated routes, not for landing page
    if(this.$route.query.lang && this.$route.path !== '/lp'){
      await helper.customPost(`${process.env.VUE_APP_API}/businesses`,{language : this.$route.query.lang })
      this.$router.push({ path: '/' })
    }

    // Skip loading and data fetching for public pages like landing page
    const publicPages = ['/lp', '/login', '/signup', '/reset-password', '/new-password', '/2fa']
    if (publicPages.includes(this.$route.path)) {
      this.state = true
      return
    }

    this.state = false
    if(this.$store.getters.TOKEN){
      await this.getBusiness()
      await this.getModules()
      
      // Initialize Mixpanel after user data is loaded
      this.initializeMixpanel()
    }

    this.state = true

    // Initialize all modules as disabled except settings
    if (this.isNew == true &&  this.modules) {
      this.modules.forEach(module => {
        if (module.module_key !== 'settings') {
          module.enabled = false;
          module.items.forEach(item => {
            item.enabled = false;
          });
        }
      });
    }
  },
  async mounted(){
    this.initializeMobileAppBanner()

    // Set RTL direction on document element
    if (this.$i18n.locale === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
      import('../public/css/styles-ar.css').then(() => {}).catch()
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  },
  watch: {
    '$i18n.locale'(newLocale) {
      // Update dir attribute when language changes
      if (newLocale === 'ar') {
        document.documentElement.setAttribute('dir', 'rtl');
      } else {
        document.documentElement.setAttribute('dir', 'ltr');
      }
    },
    user: {
      handler(newUser) {
        // Initialize Mixpanel when user data becomes available
        if (newUser && newUser.id) {
          this.initializeMixpanel();
        }
      },
      immediate: true,
      deep: true
    }
  },
    methods : {
      initializeMobileAppBanner() {
        try {
          this.mobileAppBannerDismissed = localStorage.getItem(MOBILE_APP_BANNER_DISMISSED_KEY) === '1'
        } catch (error) {
          this.mobileAppBannerDismissed = false
        }

        if (this.mobileAppBannerDismissed || this.isRunningAsInstalledApp()) return

        const userAgent = navigator.userAgent || navigator.vendor || ''
        const platform = navigator.userAgentData?.platform || navigator.platform || ''
        const isAndroid = /android/i.test(`${userAgent} ${platform}`)
        const isIOS = /iPad|iPhone|iPod/i.test(userAgent) || (/Mac/i.test(platform) && navigator.maxTouchPoints > 1)

        if (isAndroid) this.mobileAppPlatform = 'android'
        else if (isIOS) this.mobileAppPlatform = 'ios'
      },
      isRunningAsInstalledApp() {
        const capacitor = window.Capacitor
        const isNativeCapacitor = Boolean(
          capacitor && (
            (typeof capacitor.isNativePlatform === 'function' && capacitor.isNativePlatform()) ||
            (typeof capacitor.getPlatform === 'function' && capacitor.getPlatform() !== 'web')
          )
        )
        const isNativeProtocol = ['capacitor:', 'ionic:'].includes(window.location.protocol)
        const isStandalone = Boolean(
          window.matchMedia?.('(display-mode: standalone)').matches ||
          window.navigator.standalone
        )

        return isNativeCapacitor || isNativeProtocol || isStandalone
      },
      dismissMobileAppBanner() {
        this.mobileAppBannerDismissed = true
        try {
          localStorage.setItem(MOBILE_APP_BANNER_DISMISSED_KEY, '1')
        } catch (error) {
          // The in-memory state still dismisses the banner for this session.
        }
      },
      initializeMixpanel() {
        // Only initialize if window.mixpanel exists and user is loaded
        if (window.mixpanel && this.user && this.user.id) {
            window.mixpanel.identify(this.user.id);
            window.mixpanel.people.set({
              $email: this.user.email,
              $name: this.user.name
            });
        
        }
      },
      async getModules() {
        const response = await helper.customGet(`${process.env.VUE_APP_API}/modules`)
        if (response?.data?.length) {
          // Process each module and sort its items by position
          const processedModules = response.data.map(module => ({
            ...module,
            items: module.items ? 
              module.items.sort((a, b) => (a.position ?? 0) - (b.position ?? 0)) : 
              []
          }));
          
          this.$store.commit('SET_MODULES', processedModules)
        }
      },
      async getBusiness(){
        const response = await helper.customGet(`${process.env.VUE_APP_API}/businesses`)
        if(response?.data?.user){
          this.$store.commit('SET_USER',response.data.user)
        }
        if(response?.data?.business){
          this.isNew = false
          this.business.id = response.data.business.id
          this.business.name = response.data.business.name
          this.business.email = response.data.business.email
          this.business.phone = response.data.business.phone
          this.business.website = response.data.business.website
          this.business.address = response.data.business.address
          this.business.vat = response.data.business.vat
          this.business.logo = response.data.business.logo
          this.business.trial_end_at = response.data.business.trial_end_at
          this.business.status = response.data.business.status
          this.business.metadata = response.data.business.metadata
          this.business.currency = response.data.business.currency
          this.business = response.data.business
          this.$store.commit('SET_BUSINESS',this.business)
          if(response.data.user.language){
            this.business.language = response.data.user.language
            localStorage.setItem('currentLang',this.business.language)
          }
          this.$store.commit('SET_PERMISSIONS',{role : response.data.role, permissions : response.data.permissions })
        }else{
          this.isNew = true
          this.$router.push({ path: '/onboarding' })
          // if(response?.data?.user?.email){
          //   this.business.email = response.data.user.email
          // }

          // if(response?.data?.user?.name){
          //   this.business.name = `${response.data.user.name}'s Business`
          // }
          // // eslint-disable-next-line no-undef
          // this.createModal = new bootstrap.Modal(document.getElementById('checker-modal'),{backdrop: 'static', keyboard: false})
          // this.createModal.show()
        }
      },
    },
    computed : {
      user(){
        return this.$store.getters.USER 
      },
      modules() { return this.$store.getters.MODULES },
      kioskMode() { return this.$store.getters.KIOSK_MODE },
      showFeedbackToast() {
        const userCreatedAt = Date.parse(this.user?.created_at)
        const isLegacyUser = Number.isFinite(userCreatedAt) && userCreatedAt <= FEEDBACK_CAMPAIGN_CUTOFF

        return Boolean(this.$store.getters.TOKEN) && isLegacyUser && this.$route.meta.sidebar !== false && !this.kioskMode && !this.$route.path.startsWith('/admin')
      },
      showUpdateAnnouncement() {
        return Boolean(this.$store.getters.TOKEN) && !this.kioskMode && !this.$route.path.startsWith('/admin')
      },
      showMobileAppBanner() {
        return Boolean(this.$store.getters.TOKEN || this.isToken) &&
          Boolean(this.mobileAppPlatform) &&
          !this.mobileAppBannerDismissed &&
          this.$route.meta.sidebar !== false &&
          !this.kioskMode &&
          !this.$route.path.startsWith('/admin')
      },
    }
}
</script>

<style>
.form-label{
  margin-bottom: 5px !important;
}
table th, table td{
  white-space: nowrap;
}
.nav-link{
  color: #303030 !important
}
/* .active{
  background: '#303030' !important;
  color: white !important
} */
#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
  color: #2c3e50;
}

#nav a.router-link-exact-active {
  color: #42b983;
}


.custom-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100vh;
  min-height: 100dvh;
  z-index: 99900;
  position: fixed;
  top: 0;
  left: 0;
  overflow: hidden;
  padding: 32px 20px;
  background:
    radial-gradient(circle at 50% -10%, rgba(0, 74, 173, 0.1), transparent 38%),
    linear-gradient(180deg, #ffffff 0%, #f7faff 100%);
}

.loading-content {
  position: relative;
  z-index: 2;
  width: min(100%, 720px);
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-logo {
  width: 108px;
  height: 108px;
  display: block;
  border-radius: 28px;
  box-shadow: 0 20px 45px rgba(0, 74, 173, 0.2);
  animation: loading-logo-in 600ms ease-out both;
}

.loading-tagline {
  margin: 20px 0 30px;
  color: #506176;
  font-size: 1rem;
  font-weight: 500;
  letter-spacing: 0.01em;
  text-align: center;
}

.loading-modules {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.loading-module {
  width: 42px;
  height: 42px;
  flex: 0 0 42px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid var(--module-background);
  border-radius: 12px;
  color: var(--module-color);
  background: var(--module-background);
  box-shadow: 0 8px 20px rgba(27, 55, 90, 0.07);
  font-size: 1rem;
  opacity: 0;
  animation: loading-module-in 520ms ease-out forwards;
  animation-delay: var(--module-delay);
}

.loading-progress {
  width: 148px;
  height: 4px;
  margin-top: 30px;
  overflow: hidden;
  border-radius: 999px;
  background: #dce8f7;
}

.loading-progress span {
  display: block;
  width: 42%;
  height: 100%;
  border-radius: inherit;
  background: linear-gradient(90deg, #004aad, #00a98f);
  animation: loading-progress 1.35s ease-in-out infinite;
}

.loading-glow {
  position: absolute;
  border-radius: 50%;
  filter: blur(8px);
  pointer-events: none;
}

.loading-glow-primary {
  top: 8%;
  left: 8%;
  width: 220px;
  height: 220px;
  background: rgba(0, 74, 173, 0.06);
}

.loading-glow-accent {
  right: 8%;
  bottom: 6%;
  width: 180px;
  height: 180px;
  background: rgba(0, 212, 170, 0.07);
}

.loading-text {
  margin-top: 13px;
  color: #6f7e91;
  font-size: 0.82rem;
  font-weight: 500;
  text-align: center;
  max-width: 400px;
  padding: 0 1rem;
}

@keyframes loading-logo-in {
  from { opacity: 0; transform: translateY(8px) scale(0.96); }
  to { opacity: 1; transform: translateY(0) scale(1); }
}

@keyframes loading-module-in {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

@keyframes loading-progress {
  0% { transform: translateX(-110%); }
  50% { transform: translateX(75%); }
  100% { transform: translateX(250%); }
}

@media (max-width: 575.98px) {
  .custom-loading {
    padding: 24px 16px;
  }

  .loading-logo {
    width: 90px;
    height: 90px;
    border-radius: 23px;
  }

  .loading-tagline {
    margin: 16px 0 22px;
  }

  .loading-module {
    width: 38px;
    height: 38px;
    flex-basis: 38px;
    border-radius: 11px;
    font-size: 0.92rem;
  }

  .loading-progress {
    margin-top: 22px;
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-logo,
  .loading-module,
  .loading-progress span {
    animation-duration: 1ms;
    animation-iteration-count: 1;
  }
}

/* Modal Styles */
.modal-content {
  border: none;
  border-radius: 12px;
  box-shadow: 0 0 20px rgba(0,0,0,0.1);
}

.modal-header {
  border-radius: 12px 12px 0 0;
}

.nav-pills .nav-link {
  color: #6c757d;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  transition: all 0.2s ease;
}

.nav-pills .nav-link.active {
  /* background-color: #0d6efd;*/
  color: white;
}


.logo-upload-container {
  width: 60px;
  height: 60px;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  overflow: hidden;
}

.logo-upload-container:hover {
  border-color: #0d6efd;
  background-color: #f8f9fa;
}

.logo-upload-container img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.logo-loading {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255,255,255,0.8);
}

.settings-container .card {
  border: 1px solid #e9ecef;
  transition: all 0.2s ease;
}

.settings-container .card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.05);
}

.form-label {
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}


.form-control:focus, .form-select:focus {
  border-color: #0d6efd;
  box-shadow: 0 0 0 0.2rem rgba(13,110,253,0.15);
}

.alert {
  border-radius: 8px;
  border: none;
  background-color: #e7f5ff;
  color: #0c5460;
}

.btn {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  border-radius: 6px;
  font-weight: 500;
}

.btn-primary:hover {
  /*background-color: #303030;*/
  border-color: #303030;
}

.btn-outline-primary {
  color: #334155;
  border-color: #cbd5e1;
}

.btn-outline-primary:hover {
  background-color: #004aad;
  color: white;
}

.step-link {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.25rem;
    border-radius: 12px;
    transition: all 0.2s ease;
    font-size: 1.1rem;
}

.step-indicator {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    min-width: 32px;
}

.step-indicator i {
    font-size: 1.75rem;
}

.step-text {
    font-weight: 500;
    font-size: 1.1rem;
}

.nav-pills .nav-link {
    color: #6c757d;
    background: transparent;
    /*margin: 0 0.5rem;*/
}

.nav-pills .nav-link.active {
    /*background-color: #0d6efd;*/
    /*color: white;*/
    font-weight: 600;
}

.nav-pills .nav-link.completed {
    color: #198754;
}

.nav-pills .nav-link.completed .step-indicator i {
    color: #198754;
}

/* .nav-pills .nav-link.active .step-indicator i {
    color: white;
} */

/* Mobile-only: make all modal footers sticky and body scrollable */
@media (max-width: 575.98px) {
  .modal-dialog {
    margin: 0.5rem;
  }
  .modal-content {
    max-height: calc(100vh - 1rem);
    display: flex;
    flex-direction: column;
  }
  .modal-header,
  .modal-footer {
    flex-shrink: 0;
  }
  .modal-body {
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    /* Avoid content being hidden behind sticky footer */
    padding-bottom: 72px;
  }
  .modal-footer {
    position: sticky;
    bottom: 0;
    background: #fff;
    z-index: 1040; /* above body content */
    box-shadow: 0 -4px 12px rgba(0,0,0,0.06);
  }
}

/* Mobile navbar and content spacing */
@media (max-width: 991.98px) {
  /* Add padding to main content area to account for fixed navbar */
  .custom-sidebar-space,
  .custom-sidebar-full-space {
    padding-top: 70px !important;
  }

  #app.mobile-app-banner-visible .custom-sidebar-space,
  #app.mobile-app-banner-visible .custom-sidebar-full-space {
    padding-top: 134px !important;
  }
}

/* RTL support for modals */
[dir="rtl"] .modal-body,
[dir="rtl"] .modal-header,
[dir="rtl"] .modal-footer {
  text-align: right;
}

[dir="rtl"] .form-label {
  text-align: right;
  display: block;
}

[dir="rtl"] .form-check {
  text-align: right;
  padding-right: 1.5em;
  padding-left: 0;
}

[dir="rtl"] .form-check-input {
  float: right;
  margin-right: -1.5em;
  margin-left: 0;
}

[dir="rtl"] .input-group > :not(:first-child) {
  margin-right: -1px;
  margin-left: 0;
}

[dir="rtl"] .btn-close {
  margin-right: auto;
  margin-left: 0;
}

</style>
